import { existsSync, readFileSync } from "node:fs";
import { relative, join } from "node:path";
import chalk from "chalk";
import type { CaiConfig } from "./types.js";
import { findScaffoldFiles, DEFAULT_STALENESS } from "./drift/index.js";
import { extractClaims } from "./drift/claims.js";
import { batchFileGitInfo } from "./git.js";
import { globSync } from "glob";
import { estimateFileTokens } from "./utils/fs.js";
import { aggregateByFile, readQueries } from "./telemetry/query-log.js";
import { readHistory, summarizeHistory } from "./drift/history.js";

interface FileHealth {
  file: string;
  daysSinceUpdate: number | null;
  commitsSinceUpdate: number | null;
  status: "fresh" | "warn" | "stale";
  tokens: number;
  tokenStatus: "ok" | "large" | "huge";
}

interface CoverageGap {
  directory: string;
  uncoveredCount: number;
  examples: string[];
  hint: string;
}

interface TokenBudget {
  total: number;
  status: "ok" | "warn" | "critical";
  largestFiles: Array<{ file: string; tokens: number }>;
}

interface ClaudeMdBudget {
  lines: number;
  instructions: number;
  status: "ok" | "warn" | "critical";
}

interface HotFile {
  file: string;
  hits: number;
  tokens: number;
  hasDrift: boolean;
}

interface DriftTrendSummary {
  current: number;
  delta: number | null;
  best: number;
  average: number;
  sparkline: string;
  runs: number;
}

interface HealthReport {
  files: FileHealth[];
  gaps: CoverageGap[];
  overallScore: number;
  tokenBudget: TokenBudget;
  claudeMdBudget: ClaudeMdBudget | null;
  hotFiles: HotFile[];
  telemetryDays: number;
  driftTrend: DriftTrendSummary | null;
}

/** Print a token cost table for all scaffold context files. Used by `cai check --tokens`. */
export function printTokenReport(config: CaiConfig): void {
  const scaffoldFiles = findScaffoldFiles(config.projectRoot, config.scaffoldRoot);
  if (scaffoldFiles.length === 0) {
    console.log("No scaffold files found.");
    return;
  }

  const rows = scaffoldFiles.map((absPath) => {
    const file = relative(config.projectRoot, absPath);
    const tokens = estimateFileTokens(absPath);
    return { file, tokens, status: tokenStatus(tokens) };
  }).sort((a, b) => b.tokens - a.tokens);

  const total = rows.reduce((sum, r) => sum + r.tokens, 0);
  const totalStatus =
    total >= TOKEN_TOTAL_CRITICAL ? "critical" :
    total >= TOKEN_TOTAL_WARN ? "warn" : "ok";

  console.log();
  console.log("cai check --tokens — scaffold token costs");
  console.log();

  for (const row of rows) {
    const icon = row.status === "ok" ? "✔" : row.status === "large" ? "⚠" : "✖";
    const note =
      row.status === "huge"  ? " ← very large, trim or split this file" :
      row.status === "large" ? " ← consider shortening" : "";
    console.log(`${icon} ${row.file.padEnd(40)} ~${row.tokens.toLocaleString()} tokens${note}`);
  }

  console.log();
  const totalIcon = totalStatus === "ok" ? "✔" : totalStatus === "warn" ? "⚠" : "✖";
  const totalNote =
    totalStatus === "ok"       ? "lean — AI reads this cheaply" :
    totalStatus === "warn"     ? "getting large — consider trimming" :
                                 "too heavy — AI burns tokens on every session";
  console.log(`${totalIcon} Total: ~${total.toLocaleString()} tokens — ${totalNote}`);
  if (totalStatus !== "ok") {
    console.log("  ℹ Use 'cai mcp' so AI can query context on-demand instead of loading everything.");
  }
  console.log();
}

const TOKEN_FILE_LARGE = 1_500;
const TOKEN_FILE_HUGE = 3_000;
const TOKEN_TOTAL_WARN = 6_000;
const TOKEN_TOTAL_CRITICAL = 12_000;

function tokenStatus(tokens: number): FileHealth["tokenStatus"] {
  return tokens >= TOKEN_FILE_HUGE ? "huge" : tokens >= TOKEN_FILE_LARGE ? "large" : "ok";
}

const SOURCE_DIRS = ["src", "lib", "app", "packages", "internal"];
const SOURCE_EXT = "{ts,js,tsx,jsx,py,go,cs,java,rb,rs,kt,kts,swift,cpp,c,h,lua,ex,exs,scala,php,dart}";
const SOURCE_IGNORE = ["**/node_modules/**", "**/dist/**", "**/build/**", "**/.git/**", "**/bin/**", "**/obj/**", "**/target/**", "**/.gradle/**", "**/.venv/**", "**/venv/**"];

export async function runHealth(config: CaiConfig): Promise<HealthReport> {
  const { projectRoot, scaffoldRoot } = config;
  const scaffoldFiles = findScaffoldFiles(projectRoot, scaffoldRoot);
  const relPaths = scaffoldFiles.map((f) => relative(projectRoot, f));

  // Batch git info — single pass instead of N sequential calls
  const gitInfo = await batchFileGitInfo(relPaths, projectRoot);

  // Per-file freshness + token cost
  const { warnDays, errorDays, warnCommits, errorCommits } = DEFAULT_STALENESS;
  const files: FileHealth[] = scaffoldFiles.map((absPath, i) => {
    const file = relPaths[i];
    const info = gitInfo.get(file);
    const days = info?.days ?? null;
    const commits = info?.commits ?? null;
    const status: FileHealth["status"] =
      (days !== null && days > errorDays) || (commits !== null && commits > errorCommits)
        ? "stale"
        : (days !== null && days > warnDays) || (commits !== null && commits > warnCommits)
        ? "warn"
        : "fresh";
    const tokens = estimateFileTokens(absPath);
    return { file, daysSinceUpdate: days, commitsSinceUpdate: commits, status, tokens, tokenStatus: tokenStatus(tokens) };
  });

  // Coverage analysis
  const allClaims = scaffoldFiles.flatMap((f, i) => extractClaims(f, relPaths[i]));
  const claimedPaths = new Set(
    allClaims
      .filter((c) => c.kind === "path")
      .map((c) => c.value.replace(/^\//, ""))
  );

  const sourceFiles = SOURCE_DIRS.flatMap((dir) =>
    globSync(`${dir}/**/*.${SOURCE_EXT}`, { cwd: projectRoot, ignore: SOURCE_IGNORE })
  ).filter((f, i, arr) => arr.indexOf(f) === i);

  const uncovered = sourceFiles.filter((f) => !claimedPaths.has(f));
  const gaps = buildCoverageGaps(uncovered);

  // Token budget
  const totalTokens = files.reduce((sum, f) => sum + f.tokens, 0);
  const tokenBudget: TokenBudget = {
    total: totalTokens,
    status: totalTokens >= TOKEN_TOTAL_CRITICAL ? "critical" : totalTokens >= TOKEN_TOTAL_WARN ? "warn" : "ok",
    largestFiles: [...files]
      .sort((a, b) => b.tokens - a.tokens)
      .slice(0, 3)
      .filter((f) => f.tokens >= TOKEN_FILE_LARGE)
      .map((f) => ({ file: f.file, tokens: f.tokens })),
  };

  // CLAUDE.md instruction budget
  const claudeMdBudget = analyzeClaudeMdBudget(projectRoot);

  // Hot files from telemetry — top files Claude actually queries
  const TELEMETRY_DAYS = 7;
  const queries = readQueries(projectRoot, { sinceMs: Date.now() - TELEMETRY_DAYS * 24 * 60 * 60 * 1000 });
  const aggregations = aggregateByFile(queries);
  const staleSet = new Set(files.filter((f) => f.status !== "fresh").map((f) => f.file));
  const hotFiles: HotFile[] = aggregations.slice(0, 10).map((a) => ({
    file: a.file,
    hits: a.hits,
    tokens: a.tokens,
    hasDrift: staleSet.has(a.file),
  }));

  // Score: 100 - penalty for stale files - penalty for large coverage gaps
  const stalePenalty = files.filter((f) => f.status === "stale").length * 10;
  const warnPenalty = files.filter((f) => f.status === "warn").length * 3;
  const gapPenalty = Math.min(30, gaps.reduce((sum, g) => sum + Math.min(10, g.uncoveredCount), 0));
  const overallScore = Math.max(0, 100 - stalePenalty - warnPenalty - gapPenalty);

  // Drift trend from history (only when at least one cai check has run)
  const historyEntries = readHistory(projectRoot);
  const trendSummary = summarizeHistory(historyEntries);
  const driftTrend: DriftTrendSummary | null = trendSummary
    ? {
        current: trendSummary.current,
        delta: trendSummary.delta,
        best: trendSummary.best,
        average: trendSummary.average,
        sparkline: trendSummary.sparkline,
        runs: trendSummary.count,
      }
    : null;

  return { files, gaps, overallScore, tokenBudget, claudeMdBudget, hotFiles, telemetryDays: TELEMETRY_DAYS, driftTrend };
}

// Thresholds based on HumanLayer's harness engineering guidance:
// - Ideal: <60 lines (concise enough that every rule survives)
// - Acceptable: <100 lines
// - Warn: 100-300 lines (instructions start to dilute compliance)
// - Critical: >300 lines (Claude drops the lower-priority items)
const CLAUDE_MD_WARN_LINES = 100;
const CLAUDE_MD_MAX_LINES = 300;
const CLAUDE_MD_WARN_INSTRUCTIONS = 60;
const CLAUDE_MD_MAX_INSTRUCTIONS = 150;

function analyzeClaudeMdBudget(projectRoot: string): ClaudeMdBudget | null {
  const claudeMdPath = join(projectRoot, "CLAUDE.md");
  if (!existsSync(claudeMdPath)) return null;

  let content: string;
  try { content = readFileSync(claudeMdPath, "utf-8"); } catch { return null; }

  const lines = content.split("\n");
  const lineCount = lines.length;

  // Count instructions: lines starting with - or * or numbered list, inside non-comment sections
  const instructions = lines.filter((l) => {
    const trimmed = l.trim();
    return /^[-*]\s/.test(trimmed) || /^\d+\.\s/.test(trimmed);
  }).length;

  const status: ClaudeMdBudget["status"] =
    lineCount > CLAUDE_MD_MAX_LINES || instructions > CLAUDE_MD_MAX_INSTRUCTIONS ? "critical" :
    lineCount > CLAUDE_MD_WARN_LINES || instructions > CLAUDE_MD_WARN_INSTRUCTIONS ? "warn" :
    "ok";

  return { lines: lineCount, instructions, status };
}

function buildCoverageGaps(uncovered: string[]): CoverageGap[] {
  const byDir: Record<string, string[]> = {};
  for (const f of uncovered) {
    const parts = f.split("/");
    const dir = parts.length > 2 ? parts.slice(0, 2).join("/") : parts[0];
    if (!byDir[dir]) byDir[dir] = [];
    byDir[dir].push(f);
  }

  return Object.entries(byDir)
    .filter(([, files]) => files.length >= 3)
    .sort((a, b) => b[1].length - a[1].length)
    .slice(0, 8)
    .map(([dir, files]) => ({
      directory: dir,
      uncoveredCount: files.length,
      examples: files.slice(0, 3),
      hint: hintForDir(dir),
    }));
}

function hintForDir(dir: string): string {
  if (/route|controller|endpoint|api/i.test(dir)) return "Consider adding an API endpoint pattern";
  if (/model|schema|entity|migration/i.test(dir)) return "Consider documenting data models in architecture.md";
  if (/auth|session|token/i.test(dir)) return "Consider documenting the auth flow in decisions.md";
  if (/component|ui|view/i.test(dir)) return "Consider documenting component conventions";
  if (/service|provider/i.test(dir)) return "Consider documenting service layer in architecture.md";
  if (/test|spec/i.test(dir)) return "Test directory — low priority for context coverage";
  return "Consider adding context coverage for this directory";
}

export function printHealth(report: HealthReport): void {
  const scoreColor =
    report.overallScore >= 80 ? chalk.green :
    report.overallScore >= 50 ? chalk.yellow : chalk.red;

  console.log();
  console.log(chalk.bold("  cai health") + chalk.dim(" — context quality dashboard"));
  console.log();
  console.log(`  Score: ${scoreColor(chalk.bold(`${report.overallScore}/100`))}  ${chalk.dim(scoreLabel(report.overallScore))}`);
  console.log();

  // ── Token budget ────────────────────────────────────────────────────────────
  const budgetColor =
    report.tokenBudget.status === "ok"       ? chalk.green :
    report.tokenBudget.status === "warn"     ? chalk.yellow : chalk.red;
  const budgetIcon =
    report.tokenBudget.status === "ok"       ? chalk.green("✔") :
    report.tokenBudget.status === "warn"     ? chalk.yellow("⚠") : chalk.red("✖");
  const budgetNote =
    report.tokenBudget.status === "ok"       ? "lean — AI reads this cheaply" :
    report.tokenBudget.status === "warn"     ? "getting large — consider trimming" :
                                               "too heavy — AI burns tokens on every session";

  console.log(chalk.dim("  ─── Token budget ───────────────────────────────────────"));
  console.log(`  ${budgetIcon} Total scaffold cost: ${budgetColor(`~${report.tokenBudget.total.toLocaleString()} tokens`)}  ${chalk.dim(budgetNote)}`);

  if (report.tokenBudget.largestFiles.length > 0) {
    for (const f of report.tokenBudget.largestFiles) {
      const tIcon  = f.tokens >= TOKEN_FILE_HUGE ? chalk.red("✖") : chalk.yellow("⚠");
      const tNote  = f.tokens >= TOKEN_FILE_HUGE
        ? "very large — trim or split this file"
        : "large — consider shortening";
      console.log(`     ${tIcon} ${chalk.cyan(f.file.padEnd(32))} ${chalk.dim(`~${f.tokens.toLocaleString()} tokens — ${tNote}`)}`);
    }
  }
  if (report.tokenBudget.status !== "ok") {
    console.log(`     ${chalk.cyan("ℹ")} ${chalk.dim("Use ")}${chalk.white("cai mcp")}${chalk.dim(" so AI can query context on-demand instead of loading everything.")}`);
    console.log(`     ${chalk.cyan("ℹ")} ${chalk.dim("Use ")}${chalk.white("mode: summary")}${chalk.dim(" or ")}${chalk.white("mode: headings")}${chalk.dim(" in MCP before loading full files.")}`);
    console.log(`     ${chalk.cyan("ℹ")} ${chalk.dim("Add \"Keep responses concise\" to CLAUDE.md — output tokens cost 5x more than input.")}`);
  }
  console.log();

  // ── CLAUDE.md instruction budget ───────────────────────────────────────────
  if (report.claudeMdBudget) {
    const b = report.claudeMdBudget;
    const bIcon = b.status === "ok" ? chalk.green("✔") : b.status === "warn" ? chalk.yellow("⚠") : chalk.red("✖");
    const bColor = b.status === "ok" ? chalk.green : b.status === "warn" ? chalk.yellow : chalk.red;
    console.log(chalk.dim("  ─── CLAUDE.md instruction budget ───────────────────────"));
    console.log(`  ${bIcon} ${bColor(`${b.lines} lines`)}${chalk.dim(` (warn ~${CLAUDE_MD_WARN_LINES}, max ~${CLAUDE_MD_MAX_LINES})`)}  ${bColor(`${b.instructions} instructions`)}${chalk.dim(` (warn ~${CLAUDE_MD_WARN_INSTRUCTIONS}, max ~${CLAUDE_MD_MAX_INSTRUCTIONS})`)}`);
    if (b.status === "warn") {
      console.log(`     ${chalk.cyan("ℹ")} ${chalk.dim("OpenAI/HumanLayer recommend keeping CLAUDE.md under ~60 lines — every rule above that dilutes compliance.")}`);
      console.log(`     ${chalk.cyan("ℹ")} ${chalk.dim("Move details to .cai/context/ files (queried via MCP on demand) instead.")}`);
    } else if (b.status === "critical") {
      console.log(`     ${chalk.cyan("ℹ")} ${chalk.dim("Claude has ~150 instruction slots after its system prompt. You're past that — many rules are being dropped.")}`);
      console.log(`     ${chalk.cyan("ℹ")} ${chalk.dim("Move details to .cai/context/ files and keep CLAUDE.md under 100 lines.")}`);
    }
    console.log();
  }

  // ── Drift trend (history) ───────────────────────────────────────────────────
  if (report.driftTrend) {
    const t = report.driftTrend;
    const sColor = (s: number) => s >= 80 ? chalk.green : s >= 50 ? chalk.yellow : chalk.red;
    const arrow = t.delta === null ? "" :
      t.delta > 0 ? chalk.green(`▲ +${t.delta}`) :
      t.delta < 0 ? chalk.red(`▼ ${t.delta}`) :
      chalk.dim("◇ no change");
    console.log(chalk.dim(`  ─── Drift trend (${t.runs} run${t.runs !== 1 ? "s" : ""}) ────────────────────────────`));
    console.log(`  current ${sColor(t.current)(`${t.current}/100`)}  ${arrow}  ${chalk.dim(`avg ${t.average} · best ${t.best}`)}`);
    console.log(`  trend   ${chalk.cyan(t.sparkline)}  ${chalk.dim("(last 30 runs)")}`);
    console.log();
  }

  // ── Hot files (telemetry) ───────────────────────────────────────────────────
  if (report.hotFiles.length > 0) {
    console.log(chalk.dim(`  ─── Hot files (last ${report.telemetryDays} days) ──────────────────────`));
    console.log(chalk.dim("  Files Claude actually queries through MCP. Drift here hurts most."));
    for (const h of report.hotFiles) {
      const driftBadge = h.hasDrift ? chalk.red(" ⚠ drift") : "";
      console.log(
        `  ${chalk.cyan(h.file.padEnd(36))} ${chalk.dim(`${h.hits} hits · ~${h.tokens.toLocaleString()}t`)}${driftBadge}`
      );
    }
    console.log();
  }

  // ── File freshness ──────────────────────────────────────────────────────────
  console.log(chalk.dim("  ─── Context file freshness ─────────────────────────────"));
  for (const f of report.files) {
    const sym  = f.status === "fresh" ? chalk.green("✔") : f.status === "warn" ? chalk.yellow("⚠") : chalk.red("✖");
    const age  = formatAge(f.daysSinceUpdate, f.commitsSinceUpdate, f.status);
    const tHint =
      f.tokenStatus === "huge"  ? chalk.dim(` (~${f.tokens.toLocaleString()}t, trim recommended)`) :
      f.tokenStatus === "large" ? chalk.dim(` (~${f.tokens.toLocaleString()}t)`) : "";
    console.log(`  ${sym} ${chalk.cyan(f.file.padEnd(32))} ${chalk.dim(age)}${tHint}`);
  }

  // ── Coverage gaps ───────────────────────────────────────────────────────────
  const meaningfulGaps = report.gaps.filter((g) => !/test|spec/i.test(g.directory));
  if (meaningfulGaps.length > 0) {
    console.log();
    console.log(chalk.dim("  ─── Coverage gaps ──────────────────────────────────────"));
    console.log(chalk.dim("  These source directories have no scaffold references pointing to them."));
    console.log(chalk.dim("  AI agents won't have context about this code."));
    console.log();
    for (const gap of meaningfulGaps) {
      console.log(
        `  ${chalk.yellow("⚠")} ${chalk.cyan(gap.directory)}` +
        chalk.dim(` — ${gap.uncoveredCount} file${gap.uncoveredCount !== 1 ? "s" : ""} without context coverage`)
      );
      if (gap.examples.length > 0) {
        console.log(chalk.dim(`     e.g. ${gap.examples.slice(0, 2).join(", ")}`));
      }
      console.log(`     ${chalk.cyan("ℹ")} ${chalk.dim(gap.hint)}`);
    }
  }

  // ── Recommended actions ─────────────────────────────────────────────────────
  const stale = report.files.filter((f) => f.status === "stale");
  const warn  = report.files.filter((f) => f.status === "warn");
  if (stale.length > 0 || warn.length > 0) {
    console.log();
    console.log(chalk.dim("  ─── Recommended actions ────────────────────────────────"));
    for (const f of stale) {
      console.log(
        `  ${chalk.red("✖")} ${chalk.cyan(f.file)} ${chalk.dim("is stale")}` +
        chalk.dim(` — run `) + chalk.white("cai sync") + chalk.dim(" to update it with AI")
      );
    }
    for (const f of warn) {
      console.log(
        `  ${chalk.yellow("⚠")} ${chalk.cyan(f.file)} ${chalk.dim("hasn't been updated in a while")}` +
        chalk.dim(` — run `) + chalk.white("cai check") + chalk.dim(" to see if it's still accurate")
      );
    }
  }

  if (report.overallScore === 100) {
    console.log();
    console.log(`  ${chalk.green("✔")} ${chalk.green("Context is fresh, lean, and well-covered. Nothing to do.")}`);
  }

  console.log();
}

function scoreLabel(score: number): string {
  if (score === 100) return "perfect — AI has everything it needs";
  if (score >= 80)   return "good — minor drift only";
  if (score >= 50)   return "needs attention — some files are behind";
  return "critical — scaffold is significantly out of date";
}

function formatAge(days: number | null, commits: number | null, _status: FileHealth["status"]): string {
  const parts: string[] = [];
  if (days !== null) {
    if (days === 0)       parts.push("updated today");
    else if (days === 1)  parts.push("updated yesterday");
    else                  parts.push(`last updated ${days} days ago`);
  }
  if (commits !== null && commits > 0) {
    parts.push(`${commits} commit${commits !== 1 ? "s" : ""} since last edit`);
  }
  if (parts.length === 0) return "no git history";
  return parts.join(" · ");
}
